//use redux toolkit query to handle API calls

import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
} from "@reduxjs/toolkit/query/react";
import {
  Task,
  ConciseTask,
  QueryParams,
  Image,
  User,
  UserQueryParams,
  PaginationResult,
  LogQuery,
  TransactionInfo,
  TxHistoryQueryParams,
  ResetImageParams,
  WithSignature,
  ModifyImageParams,
  Subscription,
  NodeStatistics,
  EstimatedProofFee,
  EstimatedProofFeeParams,
} from "zkwasm-service-helper";

import axios from "axios";
import { RootState } from "../app/store";
import {
  AutoSubmitProof,
  AutoSubmitProofQuery,
  ERC20DepositInfo,
  NodeStatisticsQueryParams,
  PaginatedQuery,
  ProverNodesSummary,
  Round1Info,
  Round1InfoQuery,
  Round2Info,
  Round2InfoQuery,
  OnlineNodesSummary,
  TaskExternalHostTable,
  TaskExternalHostTableParams,
} from "zkwasm-service-helper/dist/mjs/interface/interface";

//https://stackoverflow.com/questions/69568818/how-to-dynamically-change-base-url-using-redux-toolkit
//Customising base query
const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  unknown
> = async (args, zkWASMApi, extraOptions) => {
  // this will get the URL in state
  const baseUrl = (zkWASMApi.getState() as RootState).endpoint.currentEndpoint
    .url;
  // RTK Query wrapper around fetch, which will use the URL from state (we do not use this as we do not use fetch)
  const rawBaseQuery = fetchBaseQuery({ baseUrl });
  return rawBaseQuery(args, zkWASMApi, extraOptions);
};

// Define a service using a base URL and expected endpoints
export const zkWASMApi = createApi({
  reducerPath: "zkwasmAPI",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getTaskById: builder.query<Task, string>({
      queryFn: async (id: string, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.getTaskDetailFromId(id)) as Task | null;
          if (result === null) {
            return { error: "Task not found!" };
          }
          return { data: result };
        } catch (err: unknown) {
          console.error("Error fetching task!");
          return { error: err };
        }
      },
    }),
    getTaskList: builder.query<PaginationResult<ConciseTask[]>, QueryParams>({
      queryFn: async (args: QueryParams, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.loadTaskList(args)) as PaginationResult<
            ConciseTask[]
          >;
          return { data: result };
        } catch (err: unknown) {
          console.error("Error fetching task list!");
          return { error: err };
        }
      },
    }),
    getTaskExternalHostTable: builder.query<
      TaskExternalHostTable,
      TaskExternalHostTableParams
    >({
      queryFn: async (args: TaskExternalHostTableParams, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.getTaskExternalHostTable(
            args,
          )) as TaskExternalHostTable;
          return { data: result };
        } catch (err: unknown) {
          console.error("Error fetching task list!");
          return { error: err };
        }
      },
    }),
    getAutoSubmitProofs: builder.query<
      PaginationResult<AutoSubmitProof[]>,
      PaginatedQuery<AutoSubmitProofQuery>
    >({
      queryFn: async (args: PaginatedQuery<AutoSubmitProofQuery>, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.queryAutoSubmitProofs(
            args,
          )) as PaginationResult<AutoSubmitProof[]>;
          return { data: result };
        } catch (err: unknown) {
          console.error("Error fetching auto submit proofs!");
          return { error: err };
        }
      },
    }),
    getRound1Infos: builder.query<
      PaginationResult<Round1Info[]>,
      PaginatedQuery<Round1InfoQuery>
    >({
      queryFn: async (args: PaginatedQuery<Round1InfoQuery>, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.queryRound1Info(
            args,
          )) as PaginationResult<Round1Info[]>;
          return { data: result };
        } catch (err: unknown) {
          console.error("Error fetching round 1 info list!");
          return { error: err };
        }
      },
    }),
    getRound2Infos: builder.query<
      PaginationResult<Round2Info[]>,
      PaginatedQuery<Round2InfoQuery>
    >({
      queryFn: async (args: PaginatedQuery<Round2InfoQuery>, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.queryRound2Info(
            args,
          )) as PaginationResult<Round2Info[]>;
          return { data: result };
        } catch (err: unknown) {
          console.error("Error fetching round 2 info list!");
          return { error: err };
        }
      },
    }),
    getImageByHash: builder.query<Image, string>({
      queryFn: async (args: string, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const image = (await helper.queryImage(args)) as Image;
          return { data: image };
        } catch (err: unknown) {
          console.error("Error fetching image!");
          return { error: err };
        }
      },
    }),
    getImageBinary: builder.query<number[], string>({
      queryFn: async (args: string, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const imageBinary = (await helper.queryImageBinary(args)) as number[];
          return { data: imageBinary };
        } catch (err: unknown) {
          console.error("Error fetching image binary!");
          return { error: err };
        }
      },
    }),
    getUserInfo: builder.query<User, UserQueryParams>({
      queryFn: async (args: UserQueryParams, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const userInfo = (await helper.queryUser(args)) as User;
          return { data: userInfo };
        } catch (err: unknown) {
          console.error("Error fetching user info!");
          return { error: err };
        }
      },
    }),
    getUserSubscription: builder.query<Subscription, UserQueryParams>({
      queryFn: async (args: UserQueryParams, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const subscription = (await helper.queryUserSubscription(
            args,
          )) as Subscription;
          return { data: subscription };
        } catch (err: unknown) {
          console.error("Error fetching subscription!");
          return { error: err };
        }
      },
    }),
    getTxHistory: builder.query<
      PaginationResult<TransactionInfo[]>,
      TxHistoryQueryParams
    >({
      queryFn: async (args: TxHistoryQueryParams, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const txs = (await helper.queryTxHistory(args)) as PaginationResult<
            TransactionInfo[]
          >;
          return { data: txs };
        } catch (err: unknown) {
          console.error("Error fetching tx history!");
          return { error: err };
        }
      },
    }),
    getDepositHistory: builder.query<
      PaginationResult<ERC20DepositInfo[]>,
      TxHistoryQueryParams
    >({
      queryFn: async (args: TxHistoryQueryParams, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const txs = (await helper.queryDepositHistory(
            args,
          )) as PaginationResult<ERC20DepositInfo[]>;
          return { data: txs };
        } catch (err: unknown) {
          console.error("Error fetching deposit history!");
          return { error: err };
        }
      },
    }),
    getTaskLogs: builder.query<string, WithSignature<LogQuery>>({
      queryFn: async (args: WithSignature<LogQuery>, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const logs = (await helper.queryLogs(args)) as string;
          return { data: logs };
        } catch (err: any) {
          console.error("Error fetching task logs!");
          return { error: err };
        }
      },
    }),
    updateImage: builder.mutation<string, WithSignature<ModifyImageParams>>({
      queryFn: async (args: WithSignature<Image>, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          let result = (await helper.modifyImage(args)) as string;
          return { data: result };
        } catch (err: any) {
          console.error("Error updating image!");
          return { error: err };
        }
      },
    }),
    resetImage: builder.mutation<string, WithSignature<ResetImageParams>>({
      queryFn: async (args: WithSignature<ResetImageParams>, api, _) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          const result = (await helper.addResetTask(args)) as string;
          return { data: result };
        } catch (err: any) {
          console.error("Error reseting image!");
          return { error: err };
        }
      },
    }),
    downloadContracts: builder.mutation<string, { md5: string }>({
      queryFn: async (args: { md5: string }, api, _) => {
        try {
          const url = (api.getState() as RootState).endpoint.currentEndpoint
            .url;
          let res = (await axios.get(url + "/contracts", {
            params: args,
          })) as any;
          return { data: res.data };
        } catch (err: any) {
          console.error("Error downloading contracts!");
          return { error: err };
        }
      },
    }),
    getNodeInfo: builder.query<
      PaginationResult<NodeStatistics[]>,
      NodeStatisticsQueryParams
    >({
      queryFn: async (args: NodeStatisticsQueryParams, api) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          let response: PaginationResult<NodeStatistics[]> =
            await helper.queryNodeStatistics(args);
          return { data: response };
        } catch (err: any) {
          console.error("Error fetching node info!");
          return { error: err };
        }
      },
    }),
    getProverNodesSummary: builder.query<ProverNodesSummary, {}>({
      queryFn: async (_, api) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          let response: ProverNodesSummary =
            await helper.queryProverNodeSummary();
          return { data: response };
        } catch (err: any) {
          console.error("Error fetching node info!");
          return { error: err };
        }
      },
    }),
    getOnlineNodesSummary: builder.query<OnlineNodesSummary, {}>({
      //With queryFn we do not need a normal query method
      queryFn: async (_, api) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;

          let response: OnlineNodesSummary =
            await helper.queryOnlineNodesSummary();

          return { data: response };
        } catch (err: any) {
          console.error("Error fetching online node summary!");
          return { error: err };
        }
      },
    }),
    getEstimatedProofFee: builder.query<
      EstimatedProofFee,
      EstimatedProofFeeParams
    >({
      queryFn: async (args: EstimatedProofFeeParams, api) => {
        try {
          const helper = (api.getState() as RootState).endpoint
            .zkWasmServiceHelper;
          let response: EstimatedProofFee =
            await helper.queryEstimateProofFee(args);
          return { data: response };
        } catch (err: any) {
          console.error("Error fetching estimated proof fee!");
          return { error: err };
        }
      },
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetTaskByIdQuery,
  useGetTaskListQuery,
  useLazyGetTaskListQuery,
  useLazyGetTaskExternalHostTableQuery,
  useGetImageByHashQuery,
  useGetImageBinaryQuery,
  useGetUserInfoQuery,
  useGetUserSubscriptionQuery,
  useGetTxHistoryQuery,
  useGetDepositHistoryQuery,
  useGetTaskLogsQuery,
  useUpdateImageMutation,
  useResetImageMutation,
  useDownloadContractsMutation,
  useGetNodeInfoQuery,
  useGetOnlineNodesSummaryQuery,
  useLazyGetNodeInfoQuery,
  useGetProverNodesSummaryQuery,
  useGetAutoSubmitProofsQuery,
  useGetRound1InfosQuery,
  useGetRound2InfosQuery,
  useLazyGetEstimatedProofFeeQuery,
} = zkWASMApi;
